import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { user, userRole, role, location, asset } from "../../../../api";
import { useDashboardContext } from "../../../../contexts/NewDashboardContext.jsx";
import NormalInput from "../../../../components/form/HorizontalNormalInput/index.jsx";
import NormalSelect from "../../../../components/form/HorizontalNormalSelect/index.jsx";
import PhoneInput from "react-phone-number-input";
import Select from "react-select";
import ConfirmationDlg from "../../../../components/ConfirmationDlg";
import SignatureCanvas from "react-signature-canvas";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "rgba(0, 0, 0, 0.48)",
    borderColor: "var(--bs-border-color)",
  }),
  input: (styles) => ({
    ...styles,
    color: "white",
  }),
  menu: (styles) => ({
    ...styles,
    color: "grey",
  }),
};

function getLeafLocationsWithPath(locations) {
  const result = [];

  function traverse(location, path) {
    const currentPath = [...path, location.location_name];

    if (!location.sublocations || location.sublocations.length === 0) {
      result.push({
        value: location.id,
        label: currentPath.join(" | "),
      });
    } else {
      location.sublocations.forEach((sublocation) =>
        traverse(sublocation, currentPath)
      );
    }
  }

  locations.forEach((location) => traverse(location, []));

  return result;
}

function AddMultipleUsers({ open, onClose, onSuccess, customers }) {
  const { selectedApp } = useDashboardContext();
  const [userEntries, setUserEntries] = useState([
    {
      email: "",
      phone: "",
      firstname: "",
      lastname: "",
      jobTitle: "",
      customer_id: "",
      password: "",
      userRole: "",
      selectedLocation: [],
      selectedAsset: [],
      signature: null,
    },
  ]);
  const [userRoles, setUserRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signatureDrawers, setSignatureDrawers] = useState({});
  const [trimmedDataURLs, setTrimmedDataURLs] = useState({});
  const signatureRefs = useRef({});

  const clearSignature = (index) => {
    if (signatureRefs.current[index]) {
      signatureRefs.current[index].clear();
    }
  };

  const trimSignature = (index) => {
    if (signatureRefs.current[index]) {
      const trimmedDataURL = signatureRefs.current[index]
        .getSignaturePad()
        .toDataURL("image/png");
      setTrimmedDataURLs((prev) => ({ ...prev, [index]: trimmedDataURL }));
      updateEntry(index, "signature", trimmedDataURL);
    }
  };

  async function getUserRole() {
    try {
      let res;
      if (selectedApp == "CGM") {
        res = await userRole.list();
      }
      if (selectedApp == "CMMS") {
        res = await role.list();
      }

      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        setUserRoles(data);
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    }
  }

  async function getLocation() {
    const res = await location.list();
    if (res.ok) {
      const body = await res.json();
      const data = body.data;
      setLocations(getLeafLocationsWithPath(data));
    }
  }

  async function getAsset() {
    const res = await asset.list();
    if (res.ok) {
      const body = await res.json();
      const data = body.data;
      setAssets(
        data.map((i) => ({
          value: i.id,
          label: i.asset_name,
        }))
      );
    }
  }

  useEffect(() => {
    getUserRole();
    // getLocation();
    // getAsset();
  }, []);

  const addNewEntry = () => {
    setUserEntries([
      ...userEntries,
      {
        email: "",
        phone: "",
        firstname: "",
        lastname: "",
        jobTitle: "",

          customer_id: "",
        password: "",
        userRole: "",
        selectedLocation: [],
        selectedAsset: [],
        signature: null,
      },
    ]);
  };

  const removeEntry = (index) => {
    if (userEntries.length > 1) {
      const newEntries = userEntries.filter((_, i) => i !== index);
      setUserEntries(newEntries);
    }
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...userEntries];
    newEntries[index][field] = value;
    setUserEntries(newEntries);
  };

  const validateEntries = () => {
    const errors = [];

    userEntries.forEach((entry, index) => {
      if (!entry.email) errors.push(`Email is required for user ${index + 1}`);
      if (!entry.firstname)
        errors.push(`First name is required for user ${index + 1}`);
      if (!entry.lastname)
        errors.push(`Last name is required for user ${index + 1}`);
      if (!entry.jobTitle)
        errors.push(`Job title is required for user ${index + 1}`);
      if (!entry.password)
        errors.push(`Password is required for user ${index + 1}`);
      if (!entry.userRole)
        errors.push(`User role is required for user ${index + 1}`);
    });

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateEntries();
    if (errors.length > 0) {
      toast.error(errors.join(", "));
      return;
    }

    setLoading(true);
    try {
      const promises = userEntries.map(async (entry) => {
        const data = {
          password: entry.password,
          email: entry.email.toLowerCase(),
          job_title: entry.jobTitle,
          firstname: entry.firstname,
          lastname: entry.lastname,
            customer_id: entry.customer_id,
          phone: String(entry.phone || ""),
          role_id: entry.userRole,
          is_staff: false,
          app: selectedApp,
          locations: entry.selectedLocation.map((i) => i.value),
          assets: entry.selectedAsset.map((i) => i.value),
        };

        return await user.create(data);
      });

      const results = await Promise.all(promises);
      const successCount = results.filter((res) => res.ok).length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`${successCount} user(s) successfully added`);
        if (failedCount > 0) {
          toast.error(`${failedCount} user(s) failed to add`);
        }
        onSuccess();
        handleClose();
      } else {
        toast.error("Failed to add any users");
      }
    } catch (error) {
      toast.error("An error occurred while adding users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUserEntries([
      {
        email: "",
        phone: "",
        firstname: "",
        lastname: "",
        jobTitle: "",
          customer_id: "",
        password: "",
        userRole: "",
        selectedLocation: [],
        selectedAsset: [],
        signature: null,
      },
    ]);
    setSignatureDrawers({});
    setTrimmedDataURLs({});
    onClose();
  };

  return (
    <>
      {/* Signature Drawer Modal */}
      {Object.keys(signatureDrawers).map((index) => (
        <ConfirmationDlg
          key={index}
          open={signatureDrawers[index]}
          btnSubmitText="Confirm"
          btnCancelText="Cancel"
          onClose={() =>
            setSignatureDrawers((prev) => ({ ...prev, [index]: false }))
          }
        >
          <div className="container">
            <h4 className="text-center">Draw Signature in the white canvas</h4>
            <SignatureCanvas
              canvasProps={{ className: "canvaSig" }}
              ref={(ref) => {
                signatureRefs.current[index] = ref;
              }}
            />
            <div className="d-flex justify-content-between">
              <button
                className="btn btn-theme"
                onClick={() => trimSignature(parseInt(index))}
              >
                <i className="fa fa-image"></i> Crop Signature
              </button>
              <button
                className="btn btn-theme"
                onClick={() => clearSignature(parseInt(index))}
              >
                <i className="fa fa-eraser"></i> Clear
              </button>
            </div>
            <div>
              {trimmedDataURLs[index] ? (
                <div
                  style={{ position: "relative", textAlign: "center" }}
                  className="border border-1 border-theme p-2 mt-1"
                >
                  <h4>Signature Preview</h4>
                  <img
                    style={{
                      filter: "invert(1)",
                      width: "70%",
                      bottom: "12px",
                    }}
                    alt="signature"
                    src={trimmedDataURLs[index]}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </ConfirmationDlg>
      ))}

      <ConfirmationDlg
        open={open}
        fullscreen={true}
        btnCancelText="Cancel"
        btnSubmitText="Add Users"
        onSubmit={handleSubmit}
        onClose={handleClose}
        loading={loading}
      >
        <div className="container-fluid">
          <h4>User</h4>

          {/* Excel-like table header */}
          <div style={{ overflowX: "auto" }}>
            <table
              className="table table-bordered"
              style={{ position: "relative" }}
            >
              <thead>
                <tr>
                  <th style={{ minWidth: "50px" }}>#</th>
                  <th style={{ minWidth: "200px" }}>Email*</th>
                  <th style={{ minWidth: "150px" }}>Phone (WhatsApp)</th>
                  <th style={{ minWidth: "120px" }}>First Name*</th>
                  <th style={{ minWidth: "120px" }}>Last Name*</th>
                  <th style={{ minWidth: "150px" }}>Job Title*</th>
                  <th style={{ minWidth: "150px" }}>Customer</th>
                  <th style={{ minWidth: "120px" }}>Password*</th>
                  <th style={{ minWidth: "150px" }}>User Role*</th>
                  {/* <th style={{ minWidth: "250px" }}>Locations</th>
                  <th style={{ minWidth: "200px" }}>Assets</th> */}
                  <th style={{ minWidth: "100px" }}>Signature</th>
                  <th style={{ minWidth: "80px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {userEntries.map((entry, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>

                    {/* Email */}
                    <td>
                      <NormalInput
                        label=""
                        value={entry.email}
                        type="email"
                        onchange={(e) =>
                          updateEntry(
                            index,
                            "email",
                            e.target.value.toLowerCase()
                          )
                        }
                        name="email"
                        placeholder="Enter email"
                      />
                    </td>

                    {/* Phone */}
                    <td>
                      <div className="phone-user">
                        <PhoneInput
                          international={true}
                          defaultCountry="SG"
                          value={entry.phone}
                          onChange={(value) =>
                            updateEntry(index, "phone", value)
                          }
                        />
                        <small>* For Alert Notification</small>
                      </div>
                    </td>

                    {/* First Name */}
                    <td>
                      <NormalInput
                        label=""
                        value={entry.firstname}
                        onchange={(e) =>
                          updateEntry(index, "firstname", e.target.value)
                        }
                        name="firstname"
                        placeholder="First name"
                      />
                    </td>

                    {/* Last Name */}
                    <td>
                      <NormalInput
                        label=""
                        value={entry.lastname}
                        onchange={(e) =>
                          updateEntry(index, "lastname", e.target.value)
                        }
                        name="lastname"
                        placeholder="Last name"
                      />
                    </td>

                    {/* Job Title */}
                    <td>
                      <NormalInput
                        label=""
                        value={entry.jobTitle}
                        onchange={(e) =>
                          updateEntry(index, "jobTitle", e.target.value)
                        }
                        name="jobtitle"
                        placeholder="Job title"
                      />
                    </td>
                    <td>
                      <NormalSelect
                        label=""
                        value={entry.customer_id}
                        onchange={(e) =>
                          updateEntry(index, "customer_id", e.target.value)
                        }
                        name="customer_id"
                        placeholder="Customer ID"
                        valueKey={"id"}
                        list={customers.map((f) => ({
                            id: f.id,
                            name: f.customer_name,
                        }))}
                      />
                    </td>

                    {/* Password */}
                    <td>
                      <NormalInput
                        label=""
                        value={entry.password}
                        type="password"
                        onchange={(e) =>
                          updateEntry(index, "password", e.target.value)
                        }
                        name="password"
                        placeholder="Password"
                      />
                    </td>

                    {/* User Role */}
                    <td>
                      <NormalSelect
                        label=""
                        onchange={(e) =>
                          updateEntry(index, "userRole", e.target.value)
                        }
                        value={entry.userRole}
                        valueKey={"id"}
                        list={userRoles.map((f) => ({
                          id: f.id,
                          name: f.rolename || f.name,
                        }))}
                      />
                    </td>

                    {/* Locations */}
                    {/* <td style={{ position: "relative" }}>
                      <div
                        className="force-transparent"
                        style={{ zIndex: 9999, position: "relative" }}
                      >
                        <Select
                          isMulti
                          styles={{
                            ...colourStyles,
                            menu: (styles) => ({
                              ...styles,
                              zIndex: 9999,
                              position: "absolute",
                              color: "grey",
                            }),
                            menuPortal: (styles) => ({
                              ...styles,
                              zIndex: 9999,
                            }),
                          }}
                          options={locations}
                          value={entry.selectedLocation}
                          onChange={(value) =>
                            updateEntry(index, "selectedLocation", value)
                          }
                          placeholder="Select..."
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </td> */}

                    {/* Assets */}
                    {/* <td style={{ position: "relative" }}>
                      <div
                        className="force-transparent"
                        style={{ zIndex: 9999, position: "relative" }}
                      >
                        <Select
                          isMulti
                          styles={{
                            ...colourStyles,
                            menu: (styles) => ({
                              ...styles,
                              zIndex: 9999,
                              position: "absolute",
                              color: "grey",
                            }),
                            menuPortal: (styles) => ({
                              ...styles,
                              zIndex: 9999,
                            }),
                          }}
                          options={assets}
                          value={entry.selectedAsset}
                          onChange={(value) =>
                            updateEntry(index, "selectedAsset", value)
                          }
                          placeholder="Select..."
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </td> */}

                    {/* Signature */}
                    <td>
                      <div>
                        <div
                          className={
                            "signature border border-1 border-theme align-items-center p-3 mb-2"
                          }
                          style={{
                            minHeight: "60px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {entry.firstname} {entry.lastname}
                        </div>
                        <hr />

                        <button
                          type="button"
                          className="btn btn-theme btn-sm"
                          onClick={() =>
                            setSignatureDrawers((prev) => ({
                              ...prev,
                              [index]: true,
                            }))
                          }
                        >
                          Draw Signature
                        </button>
                        {entry.signature && (
                          <div className="mt-2">
                            <img
                              src={entry.signature}
                              alt="signature"
                              style={{ maxWidth: "100px", maxHeight: "50px" }}
                            />
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Action */}
                    <td>
                      {userEntries.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeEntry(index)}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <button
              type="button"
              className="btn btn-outline-theme"
              onClick={addNewEntry}
            >
              + Add another row
            </button>
          </div>
        </div>
      </ConfirmationDlg>
    </>
  );
}

export default AddMultipleUsers;
